import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import MotionWrapper from '../../components/base/MotionWrapper';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const GetStartedPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const packageFromUrl = searchParams.get('package');
  
  const [formData, setFormData] = useState({
    fullName: '',
    workEmail: '',
    organizationName: '',
    roleTitle: '',
    phoneNumber: '',
    teamSize: '',
    currentTools: '',
    workflowChallenges: '',
    successVision: '',
    additionalInfo: '',
    selectedPackage: packageFromUrl || ''
  });
  
  const [isScheduled, setIsScheduled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const packageOptions = ['Foundation', 'Growth', 'Professional', 'Enterprise'];
  const teamSizeOptions = ['1–5', '6–15', '16–50', '51–200', '200+'];

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsScheduled(e.target.checked);
  };

  const isFormValid = () => {
    return (
      formData.fullName &&
      formData.workEmail &&
      formData.organizationName &&
      formData.roleTitle &&
      formData.workflowChallenges &&
      formData.successVision &&
      formData.selectedPackage &&
      isScheduled
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isScheduled) {
      alert('Please confirm that you have selected a time for your discovery call.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Save to Supabase
      const { error } = await supabase
        .from('leads')
        .insert([
          {
            full_name: formData.fullName,
            email: formData.workEmail,
            organization: formData.organizationName,
            role_title: formData.roleTitle,
            phone: formData.phoneNumber || null,
            team_size: formData.teamSize,
            current_tools: formData.currentTools || null,
            top_challenges: formData.workflowChallenges,
            success_90_days: formData.successVision,
            extra_notes: formData.additionalInfo || null,
            selected_package: formData.selectedPackage,
            source_page: 'Get Started',
            booking_confirmed: isScheduled,
            created_at: new Date().toISOString()
          }
        ]);

      if (error) {
        console.error('Error saving to Supabase:', error);
        alert('There was an error submitting your form. Please try again.');
        return;
      }

      // Send emails via Edge Function
      const { error: emailError } = await supabase.functions.invoke('send-workflow-audit-emails', {
        body: {
          type: 'get_started_intake',
          leadData: {
            full_name: formData.fullName,
            email: formData.workEmail,
            organization: formData.organizationName,
            role_title: formData.roleTitle,
            phone: formData.phoneNumber || 'Not provided',
            team_size: formData.teamSize,
            current_tools: formData.currentTools || 'Not provided',
            top_challenges: formData.workflowChallenges,
            success_90_days: formData.successVision,
            extra_notes: formData.additionalInfo || 'Not provided',
            selected_package: formData.selectedPackage,
            booking_confirmed: isScheduled
          }
        }
      });

      if (emailError) {
        console.error('Error sending emails:', emailError);
        // Don't block the user flow for email errors
      }

      // Redirect to thank you page
      navigate('/thank-you');

    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F4EF' }}>
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <MotionWrapper>
            <h1 
              className="text-4xl lg:text-5xl font-bold mb-6"
              style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
            >
              Tell us about your workflows & book your discovery call
            </h1>
            <p 
              className="text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto"
              style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
            >
              Share a few details about your team and systems, then choose a time to meet. After our call, you'll receive a formal proposal and Stripe invoice. No payment is collected on this page.
            </p>
          </MotionWrapper>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <MotionWrapper delay={100}>
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-sm border" style={{ borderColor: '#E6DACD' }}>
              
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center mb-6">
                    <i 
                      className="ri-check-circle-fill text-5xl"
                      style={{ color: '#7B6655' }}
                    ></i>
                  </div>
                  <h2 
                    className="text-3xl font-bold mb-4"
                    style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                  >
                    Thank you for your intake submission!
                  </h2>
                  <p 
                    className="text-lg leading-relaxed max-w-2xl mx-auto"
                    style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                  >
                    We've received your project details and will prepare for your discovery call. You'll receive a confirmation email shortly, and we'll follow up with a formal proposal after our meeting.
                  </p>
                </div>
              ) : (
                <>
                  {/* Selected Package Section */}
                  <div className="mb-8">
                    {packageFromUrl ? (
                      <div 
                        className="bg-green-50 border border-green-200 rounded-xl p-6"
                      >
                        <h3 
                          className="text-xl font-semibold mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                        >
                          Selected Package: {packageFromUrl}
                        </h3>
                        <p 
                          className="text-sm"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Great choice! We'll discuss this package in detail during your discovery call.
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label 
                          htmlFor="selectedPackage"
                          className="block text-lg font-semibold mb-3"
                          style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                        >
                          Select Package *
                        </label>
                        <select
                          id="selectedPackage"
                          name="selectedPackage"
                          value={formData.selectedPackage}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors pr-8"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          <option value="">Choose a package...</option>
                          {packageOptions.map((pkg) => (
                            <option key={pkg} value={pkg}>{pkg}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-8">
                    
                    {/* Step 1 Header */}
                    <div>
                      <h2 
                        className="text-2xl font-bold mb-6"
                        style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                      >
                        Step 1 – Project Details
                      </h2>
                    </div>

                    {/* Contact Information */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label 
                          htmlFor="fullName"
                          className="block text-sm font-medium mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                          placeholder="Your full name"
                        />
                      </div>
                      
                      <div>
                        <label 
                          htmlFor="workEmail"
                          className="block text-sm font-medium mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Work Email *
                        </label>
                        <input
                          type="email"
                          id="workEmail"
                          name="workEmail"
                          value={formData.workEmail}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                          placeholder="your@company.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label 
                          htmlFor="organizationName"
                          className="block text-sm font-medium mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Organization Name *
                        </label>
                        <input
                          type="text"
                          id="organizationName"
                          name="organizationName"
                          value={formData.organizationName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                          placeholder="Your company name"
                        />
                      </div>
                      
                      <div>
                        <label 
                          htmlFor="roleTitle"
                          className="block text-sm font-medium mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Role / Title *
                        </label>
                        <input
                          type="text"
                          id="roleTitle"
                          name="roleTitle"
                          value={formData.roleTitle}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                          placeholder="CEO, Operations Manager, etc."
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label 
                          htmlFor="phoneNumber"
                          className="block text-sm font-medium mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phoneNumber"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      
                      <div>
                        <label 
                          htmlFor="teamSize"
                          className="block text-sm font-medium mb-2"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          Team Size
                        </label>
                        <select
                          id="teamSize"
                          name="teamSize"
                          value={formData.teamSize}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors pr-8"
                          style={{ 
                            borderColor: '#E6DACD',
                            focusRingColor: '#7B6655',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          <option value="">Select team size...</option>
                          {teamSizeOptions.map((size) => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Project Details */}
                    <div>
                      <label 
                        htmlFor="currentTools"
                        className="block text-sm font-medium mb-2"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        What tools are you using today?
                      </label>
                      <textarea
                        id="currentTools"
                        name="currentTools"
                        value={formData.currentTools}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors resize-none"
                        style={{ 
                          borderColor: '#E6DACD',
                          focusRingColor: '#7B6655',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        placeholder="List your current software, systems, and tools..."
                        maxLength={500}
                      />
                      <div className="text-right mt-1">
                        <span 
                          className="text-xs"
                          style={{ color: '#7B6655', fontFamily: 'Inter, sans-serif' }}
                        >
                          {formData.currentTools.length}/500
                        </span>
                      </div>
                    </div>

                    <div>
                      <label 
                        htmlFor="workflowChallenges"
                        className="block text-sm font-medium mb-2"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        What are your top workflow challenges? *
                      </label>
                      <textarea
                        id="workflowChallenges"
                        name="workflowChallenges"
                        value={formData.workflowChallenges}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors resize-none"
                        style={{ 
                          borderColor: '#E6DACD',
                          focusRingColor: '#7B6655',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        placeholder="Describe your biggest bottlenecks, manual processes, or inefficiencies..."
                        maxLength={500}
                      />
                      <div className="text-right mt-1">
                        <span 
                          className="text-xs"
                          style={{ color: '#7B6655', fontFamily: 'Inter, sans-serif' }}
                        >
                          {formData.workflowChallenges.length}/500
                        </span>
                      </div>
                    </div>

                    <div>
                      <label 
                        htmlFor="successVision"
                        className="block text-sm font-medium mb-2"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        What would success 90 days after launch look like? *
                      </label>
                      <textarea
                        id="successVision"
                        name="successVision"
                        value={formData.successVision}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors resize-none"
                        style={{ 
                          borderColor: '#E6DACD',
                          focusRingColor: '#7B6655',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        placeholder="Describe your ideal outcomes, time savings, efficiency gains..."
                        maxLength={500}
                      />
                      <div className="text-right mt-1">
                        <span 
                          className="text-xs"
                          style={{ color: '#7B6655', fontFamily: 'Inter, sans-serif' }}
                        >
                          {formData.successVision.length}/500
                        </span>
                      </div>
                    </div>

                    <div>
                      <label 
                        htmlFor="additionalInfo"
                        className="block text-sm font-medium mb-2"
                        style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                      >
                        Anything we should review before the call?
                      </label>
                      <textarea
                        id="additionalInfo"
                        name="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-opacity-50 transition-colors resize-none"
                        style={{ 
                          borderColor: '#E6DACD',
                          focusRingColor: '#7B6655',
                          fontFamily: 'Inter, sans-serif'
                        }}
                        placeholder="Links to documents, specific questions, or additional context..."
                        maxLength={500}
                      />
                      <div className="text-right mt-1">
                        <span 
                          className="text-xs"
                          style={{ color: '#7B6655', fontFamily: 'Inter, sans-serif' }}
                        >
                          {formData.additionalInfo.length}/500
                        </span>
                      </div>
                    </div>

                    {/* Step 2 - Scheduling */}
                    <div className="border-t pt-8" style={{ borderColor: '#E6DACD' }}>
                      <h2 
                        className="text-2xl font-bold mb-6"
                        style={{ color: '#2B2B2B', fontFamily: 'Manrope, Inter, sans-serif' }}
                      >
                        Step 2 – Schedule your discovery call
                      </h2>
                      
                      {/* Calendly Inline Widget */}
                      <div className="mb-6">
                        <div 
                          className="calendly-inline-widget" 
                          data-url="https://calendly.com/moulderdj/workflow-audit-call"
                          style={{ minWidth: '320px', height: '700px' }}
                        ></div>
                      </div>

                      <div className="flex items-start mb-8">
                        <input
                          type="checkbox"
                          id="scheduledConfirmation"
                          checked={isScheduled}
                          onChange={handleCheckboxChange}
                          className="mt-1 mr-3 w-4 h-4 rounded border-2 focus:ring-2 focus:ring-opacity-50"
                          style={{ 
                            borderColor: '#E6DACD',
                            accentColor: '#7B6655'
                          }}
                        />
                        <label 
                          htmlFor="scheduledConfirmation"
                          className="text-sm font-medium cursor-pointer"
                          style={{ color: '#2B2B2B', fontFamily: 'Inter, sans-serif' }}
                        >
                          I have selected a time for my discovery call above. *
                        </label>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={!isFormValid() || isSubmitting}
                      className="w-full py-4 px-8 rounded-xl font-semibold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      style={{ 
                        backgroundColor: '#7B6655',
                        fontFamily: 'Manrope, Inter, sans-serif'
                      }}
                    >
                      {isSubmitting ? 'Submitting intake...' : 'Submit intake'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </MotionWrapper>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GetStartedPage;